'use client'

import * as React from 'react'

/**
 * Theme scope for portalled UI.
 *
 * Some surfaces theme themselves with a *scoped* class rather than a global
 * one — `.app-dark` on the music module inside the app shell, `.canticle-scope`
 * on the public parish tree. Both are expressed as descendant selectors:
 *
 *   @custom-variant app-dark (&:where(.app-dark, .app-dark *));
 *   .app-dark input { color: #f3f4f6 }
 *
 * Radix portals (Sheet, and anything else that renders through one) mount on
 * `document.body`, OUTSIDE the element carrying that class. Every `app-dark:`
 * class inside a portal is therefore dead — silently, with no build error. What
 * you get is the light fallback on a surface the app expects to be dark, and
 * any element that never set an explicit color inherits the app's light
 * `--color-foreground` and turns invisible.
 *
 * The fix is to re-apply the scope class to the portalled content itself. The
 * variants above are written `:where(.app-dark, .app-dark *)` precisely so the
 * scope element matches its own rules, so this is enough — no `container` prop,
 * so the portal keeps rendering at the body and no layout/stacking behavior
 * changes.
 *
 * Wrap the same element that carries the class:
 *
 *   <ThemeScopeProvider scope="app-dark">
 *     <div className="app-dark">…</div>
 *   </ThemeScopeProvider>
 */
const ThemeScopeContext = React.createContext<string>('')

export function ThemeScopeProvider({ scope, children }: { scope: string; children: React.ReactNode }) {
  return <ThemeScopeContext.Provider value={scope}>{children}</ThemeScopeContext.Provider>
}

/** The scope class to re-apply on portalled content ('' when unscoped). */
export function useThemeScope(): string {
  return React.useContext(ThemeScopeContext)
}
